import React, { useEffect, useState } from 'react';
import { View, Text, Image, TouchableOpacity, StyleSheet, TextInput } from 'react-native';

export interface BookingPlatformsRowProps {
  mode: 'view' | 'edit';
  supabaseClient?: any;
  cardId?: string;
  displayVariant?: 'restaurant' | 'lodging';
  platformProductId?: string;
  platformProductIds?: string[];
  // fallback editable text (manual booking tips)
  value?: string;
  onChange?: (v: string | undefined) => void;
  // Tab already indicates context; hide the left label and use full-width list
  hideLabel?: boolean;
  // Optional card title for template variable replacement (避免权限问题)
  cardTitle?: string;
  cardAddress?: string;
  labels?: {
    loading?: string;
    noOptions?: string;
    book?: string;
    bookingAdvice?: string;
    exampleAdvice?: string;
    searchOn?: (brand: string) => string;
  };
}

const logoForSource = (source?: string, display_config?: any) => {
  if (display_config && display_config.logo_url) return { uri: display_config.logo_url } as any;
  const s = (source || '').toLowerCase();
  const bySource: Record<string, string> = {
    kkday: '/assets/partners/kkday.png',
    klook: '/assets/partners/klook.png',
    trip: '/assets/partners/trip.png',
    booking: '/assets/partners/booking.png',
    agoda: '/assets/partners/agoda.png',
  };
  const path = bySource[s];
  return path ? { uri: path } : null;
};

const deriveSource = (product: any): string | undefined => {
  // Try explicit fields first
  const direct = (product && (product.source || product.platform || product.vendor)) as string | undefined;
  if (direct) return String(direct).toLowerCase();
  const ec = product?.embed_config;
  const fromEmbed = (ec && (ec.platform || ec.source)) as string | undefined;
  if (fromEmbed) return String(fromEmbed).toLowerCase();
  const dc = product?.display_config;
  const fromDisplay = (dc && (dc.platform || dc.source)) as string | undefined;
  if (fromDisplay) return String(fromDisplay).toLowerCase();
  // Fallback: infer from link domain
  const link: string | undefined = product?.link || product?.target_url;
  if (link) {
    try {
      const u = new URL(link);
      const h = u.hostname.replace(/^www\./, '').toLowerCase();
      if (h.includes('kkday')) return 'kkday';
      if (h.includes('klook')) return 'klook';
      if (h.includes('booking')) return 'booking';
      if (h.includes('agoda')) return 'agoda';
      if (h.includes('trip.com') || h.includes('ctrip')) return 'trip';
    } catch (e) {}
  }
  return undefined;
};

const linkFrom = (obj: any): string | undefined => {
  if (!obj) return undefined;
  return obj.link || obj.url || obj.href || obj.target_url || obj?.attributes?.href;
};

// Process template product by replacing variables in URLs
const processTemplateProduct = async (product: any, cardId: string | undefined, supabaseClient: any, providedTitle?: string, providedAddress?: string): Promise<any> => {
  let title = '';
  let address = '';
  
  // 优先使用传入的参数，避免数据库查询权限问题
  if (providedTitle) {
    title = providedTitle;
    address = providedAddress || '';
  } else if (cardId && supabaseClient) {
    try {
      // 备用方案：尝试查询数据库
      const { data: cardData, error: cardErr } = await supabaseClient
        .from('cards_cards')
        .select('title, address, description')
        .eq('id', cardId)
        .eq('status', 'active')
        .single();
      
      if (cardErr || !cardData) {
        // 最后备用方案：使用页面标题
        const pageTitle = typeof document !== 'undefined' ? document.title : '';
        title = pageTitle || 'search';
      } else {
        title = cardData.title || '';
        address = cardData.address || '';
      }
    } catch (e) {
      // 查询失败，使用页面标题
      const pageTitle = typeof document !== 'undefined' ? document.title : '';
      title = pageTitle || 'search';
    }
  } else {
    // 没有足够信息，使用页面标题
    const pageTitle = typeof document !== 'undefined' ? document.title : '';
    title = pageTitle || 'search';
  }
  
  try {
    // Process embed_config items to replace template variables
    const processedProduct = { ...product };
    let embedConfig = product.embed_config;
    
    // Parse embed_config if it's a JSON string
    try {
      if (typeof embedConfig === 'string' && embedConfig.trim()) {
        embedConfig = JSON.parse(embedConfig);
      }
    } catch (e) {
      // ignore parse errors
    }
    
    // Handle array format {items: [...]}
    if (embedConfig && embedConfig.items && Array.isArray(embedConfig.items)) {
      const processedItems = embedConfig.items.map((item: any) => ({
        ...item,
        link: item.link ? 
          item.link.replace(/{title}/g, encodeURIComponent(title)).replace(/{address}/g, encodeURIComponent(address)) : 
          item.link
      }));
      
      processedProduct.embed_config = {
        ...embedConfig,
        items: processedItems
      };
    } else if (embedConfig && embedConfig.link) {
      // Handle direct object format
      processedProduct.embed_config = {
        ...embedConfig,
        link: embedConfig.link.replace(/{title}/g, encodeURIComponent(title)).replace(/{address}/g, encodeURIComponent(address))
      };
    }
    
    return processedProduct;
  } catch (e) {
    console.error('[processTemplateProduct] Error processing template:', e);
    return product;
  }
};

const expandProduct = (product: any): any[] => {
  if (!product) return [];
  let ec = product.embed_config;
  // embed_config may be stored as a JSON string in some DB rows. Try to parse it safely.
  try {
    if (typeof ec === 'string' && ec && ec.trim()) {
      const parsed = JSON.parse(ec);
      ec = parsed;
    }
  } catch (e) {
    // ignore parse errors and fall back to original ec
  }

  const items = Array.isArray(ec) ? ec : (ec && Array.isArray(ec.items) ? ec.items : null);
  if (items && items.length) {
    return items.map((it: any, idx: number) => ({
      ...product,
      id: `${product.id}#${idx}`,
      // Prefer title from the embed item (it) or its display_config, then fall back to product.title
      title: (it && typeof it.title === 'string' && it.title.trim()) ? it.title : ((it && it.display_config && it.display_config.title) ? it.display_config.title : product?.title),
      source: (it.platform || it.source || product.source || deriveSource(product)),
      link: linkFrom(it) || linkFrom(product),
      display_config: it.display_config || product.display_config,
      analytics: it.analytics || product.analytics,
      embed_config: it.embed_config || it,
    }));
  }
  return [{ ...product, source: product.source || deriveSource(product), link: linkFrom(product) }];
};

const BookingPlatformsRow: React.FC<BookingPlatformsRowProps> = ({
  mode,
  supabaseClient,
  cardId,
  displayVariant = 'restaurant',
  platformProductId,
  platformProductIds,
  value,
  onChange,
  hideLabel,
  cardTitle,
  cardAddress,
  labels,
}) => {
  const [bookingPlatforms, setBookingPlatforms] = useState<any[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [hasExplicitAssociations, setHasExplicitAssociations] = useState<boolean>(false);
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value || '');
  useEffect(() => setVal(value || ''), [value]);

  const handleSave = () => {
    setEditing(false);
    const v = (val || '').toString().trim();
    onChange && onChange(v.length ? v : undefined);
  };

  const handleBookingClick = (platform: any) => {
    try {
      let url = linkFrom(platform) || linkFrom(platform?.embed_config) || linkFrom(platform?.embed_config?.items?.[0]);
      const cid = platform.analytics?.cid;
      if (cid && url) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}cid=${encodeURIComponent(cid)}`;
      }
      if (typeof window !== 'undefined') {
        window.open(url, '_blank');
      }
      if (supabaseClient) {
        (async () => {
          try {
            await supabaseClient
              .from('promotion_clicks')
              .insert([{ promotion_id: platform.id, user_id: null, cid, source: platform.source, clicked_at: new Date().toISOString() }]);
          } catch (e) {
            console.warn('[BookingPlatformsRow] Failed to log click:', e);
          }
        })();
      }
    } catch (e) {
      console.error('[BookingPlatformsRow] Error opening booking link:', e);
    }
  };

  useEffect(() => {
    if (!supabaseClient || mode !== 'view') return;
    const load = async () => {
      // reset state on each load to avoid showing stale platforms
      setBookingPlatforms([]);
      setHasExplicitAssociations(false);
      try {
        setLoadingPlatforms(true);
        // Resolve platform product ids: prefer props, but if missing and we have a cardId,
        // try to resolve from the cards_cards row (base_payload or top-level).
        const isMeaningfulId = (v: any) => {
          if (v === null || typeof v === 'undefined') return false;
          try {
            const s = String(v).trim();
            if (!s) return false;
            const low = s.toLowerCase();
            if (low === 'null' || low === 'undefined' || low === '0') return false;
            return true;
          } catch (e) { return false; }
        };

        let resolvedPlatformProductId: string | undefined = isMeaningfulId(platformProductId) ? String(platformProductId) : undefined;
        let resolvedPlatformProductIds: string[] | undefined = Array.isArray(platformProductIds) && platformProductIds.filter(isMeaningfulId).length ? platformProductIds.filter(isMeaningfulId).map(String) : undefined;
        if ((!resolvedPlatformProductId || resolvedPlatformProductId === '') && (!resolvedPlatformProductIds || resolvedPlatformProductIds.length === 0) && cardId) {
          try {
            const { data: cardRow, error: cardErr } = await supabaseClient
              .from('cards_cards')
              .select('platform_product_id, base_payload')
              .eq('id', cardId)
              .maybeSingle();
            if (!cardErr && cardRow) {
              try {
                const candidateSingle = cardRow.platform_product_id || (cardRow.base_payload && cardRow.base_payload.platform_product_id);
                if (!resolvedPlatformProductId && isMeaningfulId(candidateSingle)) {
                  resolvedPlatformProductId = String(candidateSingle);
                }
              } catch (e) {}
              try {
                const candidateList = (cardRow.base_payload && Array.isArray(cardRow.base_payload.platform_product_ids)) ? cardRow.base_payload.platform_product_ids.filter(isMeaningfulId).map(String) : undefined;
                if ((!resolvedPlatformProductIds || resolvedPlatformProductIds.length === 0) && Array.isArray(candidateList) && candidateList.length) {
                  resolvedPlatformProductIds = candidateList;
                }
              } catch (e) {}
            }
          } catch (e) {
            // ignore
          }
        }
        if (resolvedPlatformProductIds && resolvedPlatformProductIds.length > 0) {
          // When explicit ids are provided, always treat this as authoritative.
          // Do not fall back to global table load if the ids fetch fails or returns none.
          try {
            const { data: products, error: pErr } = await supabaseClient
              .from('platform_products')
              .select('*')
              .in('id', resolvedPlatformProductIds);
            if (pErr) {
              // Log non-PGRST116 errors, but in any case do not fallback to global load.
              const code = (pErr && (pErr.code || pErr.status || pErr.name)) as any;
              if (code !== 'PGRST116' && code !== 406) {
                console.warn('[BookingPlatformsRow] load by ids error:', pErr);
              }
              setBookingPlatforms([]);
              // mark that the card did have explicit associations (even though none loaded)
              setHasExplicitAssociations(true);
              setLoadingPlatforms(false);
              return;
            }
            const now = new Date();
            const activeProducts = (products || []).filter((p: any) => p.status === 'active' && (!p.valid_until || new Date(p.valid_until) > now));
            if (activeProducts.length) {
              const activeExpanded = activeProducts.flatMap(expandProduct);
              setBookingPlatforms(activeExpanded);
              setHasExplicitAssociations(true);
            } else {
              // explicit ids provided but no active products found — show empty, do not fallback
              setBookingPlatforms([]);
              setHasExplicitAssociations(true);
            }
            setLoadingPlatforms(false);
            return;
          } catch (e) {
            // On unexpected errors, avoid fallback and show empty results.
            setBookingPlatforms([]);
            setHasExplicitAssociations(true);
            setLoadingPlatforms(false);
            return;
          }
        }
        if (resolvedPlatformProductId && (!resolvedPlatformProductIds || resolvedPlatformProductIds.length === 0)) {
          const { data: product, error: pErr } = await supabaseClient
            .from('platform_products')
            .select('*')
            .eq('id', resolvedPlatformProductId)
            .single();
          if (pErr) {
            const code = (pErr && (pErr.code || pErr.status || pErr.name)) as any;
            if (code === 'PGRST116' || code === 406) {
              // none found
            } else {
              console.warn('[BookingPlatformsRow] load by id error:', pErr);
            }
            // Do not fall back to global table load when an explicit id was provided but failed to load.
            setBookingPlatforms([]);
            setHasExplicitAssociations(false);
            setLoadingPlatforms(false);
            return;
          } else if (product) {
            const active = product.status === 'active' && (!product.valid_until || new Date(product.valid_until) > new Date());
            if (active) {
              setBookingPlatforms(expandProduct(product));
              setHasExplicitAssociations(true);
              // Explicitly return: when a card explicitly points to a product, only show that product.
              setLoadingPlatforms(false);
              return;
            }
            // If the explicit product is not active, show nothing — do not fall back to global list.
            setBookingPlatforms([]);
            setHasExplicitAssociations(false);
            setLoadingPlatforms(false);
            return;
          }
        }
        // If we don't have any resolved platform product id(s), use the fallback template
        if ((!resolvedPlatformProductId || resolvedPlatformProductId === '') && (!resolvedPlatformProductIds || resolvedPlatformProductIds.length === 0)) {
          // Use fallback template ID for cards without explicit platform products
          const fallbackTemplateId = '0aa091a5-1faf-4eed-89b6-13c4d491a636';
          try {
            const { data: fallbackProduct, error: fallbackErr } = await supabaseClient
              .from('platform_products')
              .select('*')
              .eq('id', fallbackTemplateId)
              .single();
            
            if (!fallbackErr && fallbackProduct) {
              const active = fallbackProduct.status === 'active' && (!fallbackProduct.valid_until || new Date(fallbackProduct.valid_until) > new Date());
              
              if (active) {
                // Process template URLs by replacing {title} with card title
                const processedProduct = await processTemplateProduct(fallbackProduct, cardId, supabaseClient, cardTitle, cardAddress);
                const expandedProducts = expandProduct(processedProduct);
                
                setBookingPlatforms(expandedProducts);
                setHasExplicitAssociations(true); // Show in hideLabel mode
                setLoadingPlatforms(false);
                return;
              }
            }
          } catch (e) {
            console.warn('[BookingPlatformsRow] Failed to load fallback template:', e);
          }
          try { setBookingPlatforms([]); setHasExplicitAssociations(false); } catch (e) {}
          return;
        }

        // (legacy) global load: only reached when explicit ids were present but earlier fetchs failed to return
        const { data: platforms, error } = await supabaseClient
          .from('platform_products')
          // Be permissive to schema variations; select all columns to avoid 42703 on missing fields
          .select('*');
        if (error) {
          console.warn('[BookingPlatformsRow] load active error:', error);
          return;
        }
        const positionKey = displayVariant === 'lodging' ? 'hotel_cards' : 'restaurant_cards';
        const now = new Date();
        const haveAnyPosition = (platforms || []).some((p: any) => typeof p?.display_config?.position === 'string' && p.display_config.position.length > 0);
        let relevant = (platforms || [])
          .filter((p: any) => p.status === 'active' && (!p.valid_until || new Date(p.valid_until) > now))
          .filter((p: any) => !haveAnyPosition || String((p.display_config?.position) || '').toLowerCase() === positionKey)
          .flatMap(expandProduct)
          .filter((p: any) => !!linkFrom(p));
        const existing = new Set(bookingPlatforms.map(p => p.id));
        relevant = [...bookingPlatforms, ...relevant.filter(p => !existing.has(p.id))];
        // Ensure explicit resolvedPlatformProductId is included even if position filtering would exclude it.
        try {
          if (typeof resolvedPlatformProductId === 'string' && resolvedPlatformProductId) {
            const hasExplicit = (relevant || []).some((p: any) => String(p.id || '').startsWith(resolvedPlatformProductId));
            if (!hasExplicit) {
              try {
                // Fetch the explicit product and, if active, include its expanded items at the front
                const { data: explicitProd, error: explicitErr } = await supabaseClient
                  .from('platform_products')
                  .select('*')
                  .eq('id', resolvedPlatformProductId)
                  .maybeSingle();
                if (!explicitErr && explicitProd) {
                  const now2 = new Date();
                  const active2 = explicitProd.status === 'active' && (!explicitProd.valid_until || new Date(explicitProd.valid_until) > now2);
                  if (active2) {
                    const expanded = expandProduct(explicitProd) || [];
                    // Prepend explicit expanded items so they appear first
                    relevant = [...expanded.filter((e: any) => !!linkFrom(e)), ...relevant];
                  }
                }
              } catch (e) { /* ignore fetch errors */ }
            }
          }
        } catch (e) {}
        relevant.sort((a: any, b: any) => {
          const pa = (a.display_config && a.display_config.priority) ?? 9999;
          const pb = (b.display_config && b.display_config.priority) ?? 9999;
          if (pa !== pb) return pa - pb;
          return String((a.source || deriveSource(a) || '')).localeCompare(String((b.source || deriveSource(b) || '')));
        });
        setBookingPlatforms(relevant);
      } catch (e) {
        console.error('[BookingPlatformsRow] load error:', e);
      } finally {
        setLoadingPlatforms(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, supabaseClient, mode, platformProductId, platformProductIds, displayVariant]);

  if (mode === 'view' && !editing) {
    // Compact, full-width list mode without left label (for Booking tab)
    if (hideLabel) {
      return (
        <View style={styles.bookingListContainer}>
          {loadingPlatforms ? (
            <Text style={styles.rowValue}>{labels?.loading || '載入中...'}</Text>
          ) : bookingPlatforms.length > 0 ? (
            bookingPlatforms.map((platform) => {
              // Only render expanded platforms when we have explicit associations
              if (!hasExplicitAssociations) return null;
              const src = (platform.source || deriveSource(platform) || '').toLowerCase();
              const brandMap: Record<string, string> = { kkday: 'KKday', klook: 'Klook', trip: 'Trip.com', booking: 'Booking.com', agoda: 'Agoda' };
              const brandName = brandMap[src] || src;
              let title: string = platform?.display_config?.title || platform?.title;
              if (!title) {
                 title = labels?.searchOn ? labels.searchOn(brandName) : (brandName || '預訂');
              }
              const logo = logoForSource(src, platform.display_config);
              return (
                <TouchableOpacity
                  key={platform.id}
                  style={styles.bookingListItemFull}
                  onPress={() => handleBookingClick(platform)}
                  activeOpacity={0.7}
                >
                  {logo ? <Image source={logo as any} style={styles.bookingListItemLogo} /> : null}
                  <View style={styles.bookingListTextWrap}>
                    <Text style={styles.bookingListTextPrimary}>{title}</Text>
                  </View>
                  <Text style={styles.bookingListChevron}>›</Text>
                </TouchableOpacity>
              );
            })
          ) : value ? (
            <Text style={styles.rowValue}>{value}</Text>
          ) : (
            <Text style={[styles.rowValue, styles.inlinePlaceholder]}>{labels?.noOptions || '暫無預訂選項'}</Text>
          )}
        </View>
      );
    }
    return (
      <View style={styles.row}>
        <View style={styles.rowLabelWrap}><Text style={styles.rowLabel}>{labels?.book || '預訂'}</Text></View>
        <View style={styles.rowValueWrap}>
          {loadingPlatforms ? (
            <Text style={styles.rowValue}>{labels?.loading || '載入中...'}</Text>
          ) : bookingPlatforms.length > 0 ? (
            <View style={[styles.rowValueWrapStretch, styles.bookingList]}>
                {hasExplicitAssociations ? bookingPlatforms.map((platform) => {
                const src = (platform.source || deriveSource(platform) || '').toLowerCase();
                const brandMap: Record<string, string> = { kkday: 'KKday', klook: 'Klook', trip: 'Trip.com', booking: 'Booking.com', agoda: 'Agoda' };
                const brandName = brandMap[src] || src;
                let title: string = platform?.display_config?.title || platform?.title;
                if (!title) {
                   title = labels?.searchOn ? labels.searchOn(brandName) : (brandName || '預訂');
                }
                const logo = logoForSource(src, platform.display_config);
                return (
                  <TouchableOpacity
                    key={platform.id}
                    style={styles.bookingListItem}
                    onPress={() => handleBookingClick(platform)}
                    activeOpacity={0.7}
                  >
                    {logo ? <Image source={logo as any} style={styles.bookingListItemLogo} /> : null}
                    <View style={styles.bookingListTextWrap}>
                      <Text style={styles.bookingListTextPrimary}>{title}</Text>
                    </View>
                    <Text style={styles.bookingListChevron}>›</Text>
                  </TouchableOpacity>
                );
                }) : null}
            </View>
          ) : value ? (
            <Text style={styles.rowValue}>{value}</Text>
          ) : (
            <Text style={[styles.rowValue, styles.inlinePlaceholder]}>{labels?.noOptions || '暫無預訂選項'}</Text>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.row}>
      <View style={styles.rowLabelWrap}><Text style={styles.rowLabel}>{labels?.bookingAdvice || '預訂建議'}</Text></View>
      <View style={styles.rowValueWrap}>
        {editing ? (
          <TextInput
            autoFocus
            value={val}
            onBlur={handleSave}
            onSubmitEditing={handleSave}
            onChangeText={setVal}
            style={styles.input as any}
            placeholder={labels?.exampleAdvice || "例如 建議預訂"}
            returnKeyType="done"
          />
        ) : (
          <TouchableOpacity onPress={() => { if (mode === 'edit') setEditing(true); }} activeOpacity={0.7}>
            {val && String(val).length ? (
              <Text style={styles.rowValue}>{val}</Text>
            ) : (
              <Text style={[styles.rowValue, styles.inlinePlaceholder]}>{labels?.exampleAdvice || "例如 建議預訂"}</Text>
            )}
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderColor: '#F3F4F6' },
  rowLabelWrap: { width: 180 },
  rowLabel: { color: '#6B7280' },
  rowValueWrap: { flex: 1, alignItems: 'flex-end' },
  rowValueWrapStretch: { alignItems: 'stretch', alignSelf: 'stretch' },
  rowValue: { fontWeight: '700' },
  inlinePlaceholder: { color: '#A3AAB3' },
  bookingListContainer: { width: '100%', gap: 10 },
  // Old horizontal logo row replaced by vertical list items
  bookingList: { gap: 8 },
  bookingListItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 8, borderWidth: 1, borderColor: '#EEF0F3', borderRadius: 8 },
  bookingListItemFull: { width: '100%', flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 12, borderWidth: 1, borderColor: '#EEF0F3', borderRadius: 12 },
  bookingListItemLogo: { width: 24, height: 24, borderRadius: 4, marginRight: 10 },
  bookingListTextWrap: { flex: 1 },
  bookingListTextPrimary: { fontWeight: '700', color: '#111827' },
  bookingListChevron: { marginLeft: 8, color: '#9CA3AF', fontSize: 18, lineHeight: 18 },
  // Legacy styles kept for potential reuse
  bookingButtonGroup: { flexDirection: 'row', flexWrap: 'nowrap', gap: 10, justifyContent: 'flex-end' },
  bookingLogoBtn: { backgroundColor: 'transparent', paddingHorizontal: 0, paddingVertical: 0 },
  bookingLogo: { width: 28, height: 28, borderRadius: 4 },
  input: { marginLeft: 8, color: '#8E8E93', borderBottomWidth: 1, borderColor: '#F3F4F6', paddingVertical: 4 },
});

export default BookingPlatformsRow;
